import { Preferences } from '@capacitor/preferences';
import { publishAppEvent } from '../shared/appEvents/appEventBus';
import {
  clearBackRoute,
  clearCompletionRoute,
  clearJourney,
  clearStrongerNudgeId,
  clearStrongerNudgeStageStack,
} from '../stronger-nudge/state/strongerNudgeIdStorage';
import { clearPendingRedirect } from './state/pendingRedirect';
import {
  getTrustedDeviceToken,
  setTrustedDeviceToken,
  TRUSTED_DEVICE_TOKEN_KEY,
} from './state/trustedDevice';
import { clearPendingConversion } from '@/domains/transfers/addATransfer/state/pendingConversion';
import { INTRO_SCREENS_SEEN_KEY } from '../intro-screens/constants';
import { THEME_PREFERENCE_KEY } from '../theme/constants';

import {
  Auth,
  UserCredential,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signOut,
  type User,
} from 'firebase/auth';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useExchangeCredentialsForLoginTokenMutation } from '@/domains/auth/api/mutations/useExchangeCredentialsForLoginTokenMutation';
import { useQueryClient } from '@tanstack/react-query';

import { useThrottledCallback } from 'use-debounce';

import {
  calculateTimeLeft,
  getCustomExpiryTime,
  getFirebaseTokenFromLocalStorage,
} from '@/domains/auth/utils/customSession';

import { fetchRemainingSessionTime } from '@/domains/auth/utils/fetchRemainingSessionTime';
import { useRouter } from 'next/router';
import { pushEventToDataLayer } from '../shared/analytics/google-tag-manager/googleTagManager';
import { disableIterableDevice } from '../shared/push-notifications/pushService';
import {
  AuthErrorCode,
  getAuthErrorCode,
} from '@/domains/auth/api/responseHelpers/error';
import { useCheckEmailSync } from '@/domains/auth/api/mutations/useCheckEmailSync';
import { isNative } from '@/domains/shared/utils/native';

const CUSTOM_EXPIRY_TIME = getCustomExpiryTime();

export const getShareAuthWithWA = () =>
  process.env.NEXT_PUBLIC_WA_SAME_ORIGIN === 'true' && !isNative();

type ResetPasswordResult =
  | { result: 'navigate'; err?: never }
  | { result: 'error'; err: string };

export type LoginResult =
  | {
      resultStatus: 'ERROR';
      user: null;
      err: AuthErrorCode;
    }
  | {
      resultStatus: 'OTP_SENT';
      validatedMobilePhone: string;
      user: null;
      err: null;
    }
  | {
      resultStatus: 'SUCCESSFUL_LOGIN_NO_2FA' | 'SUCCESSFUL_LOGIN_2FA';
      user: UserCredential;
      err: null;
      offer: string | null;
      landingPageOffer: string | null;
    };

interface AuthState {
  auth: Auth | undefined;
  isPending: boolean;
  isSuccess: boolean;
  isLoggedIn: boolean;
  currentUser: User | null;
  authReady: boolean;
  login: (
    email: string,
    password: string,
    otp?: string,
    landingPagePath?: string,
    trustDevice?: boolean,
  ) => Promise<LoginResult>;
  logout: () => Promise<{ success: boolean; err?: string }>;
  resetPassword: (email: string) => Promise<ResetPasswordResult>;
  getIdToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

const AuthProvider: React.FC<{ children: React.ReactNode; auth?: Auth }> = ({
  auth,
  children,
}) => {
  const queryClient = useQueryClient();
  const [isPending, setIsPending] = useState<boolean>(true);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [firebaseAuthReady, setFirebaseAuthReady] = useState(false);
  // `authReady` means "Firebase has settled its initial auth state and we
  // can trust `currentUser` / `isLoggedIn`". Migration is handled
  // separately by `IdentityBootProvider`, which has its own `phase === 'ready'`
  // signal that PIN/biometrics gate on.
  const authReady = firebaseAuthReady;
  const [isSignUpPath, setIsSignUpPath] = useState(false);

  const router = useRouter();

  const { mutateAsync: fetchLoginToken } =
    useExchangeCredentialsForLoginTokenMutation();

  const { mutateAsync: checkEmailSync } = useCheckEmailSync();

  const logoutTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const clearLogoutTimer = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  };
  const restartLogoutTimer = useThrottledCallback(
    async () => {
      if (!currentUser) {
        await logout();
        return;
      }
      clearLogoutTimer();

      // this needs to be from local storage as we do not want to trigger
      // a refresh
      let token = getFirebaseTokenFromLocalStorage();
      if (!token) {
        await logout();
        return;
      }
      const { remaining } = await fetchRemainingSessionTime(token);
      const sec = Math.floor(remaining / 1000);
      const min = Math.floor(sec / 60);
      console.info(`Restarting logout timer, time left: ${min}m ${sec % 60}s`);
      let timeLeft = remaining;
      if (timeLeft <= 0) {
        await logout();
        return;
      }
      // force refresh at most every 5 minutes
      if (timeLeft < CUSTOM_EXPIRY_TIME - 5 * 60 * 1000) {
        token = await currentUser.getIdToken(true);
        const refreshedSession = await fetchRemainingSessionTime(token);
        timeLeft = refreshedSession.remaining;
        const refreshedSec = Math.floor(timeLeft / 1000);
        const refreshedMin = Math.floor(refreshedSec / 60);
        console.info(
          `Token refreshed. New time left: ${refreshedMin}m ${refreshedSec % 60}s`,
        );
      }
      logoutTimerRef.current = setTimeout(() => {
        logout();
      }, timeLeft);
    },
    60000,
    { trailing: false, leading: true }, // reset immediately, then throttle for 1 minute
  );

  const logoutOfWA = async () => {
    if (!getShareAuthWithWA()) {
      return;
    }
    const response = await fetch('/api/auth/logout', {
      method: 'POST',
    });
    if (!response.ok) {
      throw new Error('Failed to logout from server');
    }
    const result = await response.json();
    if (!result.success) {
      throw new Error('Failed to logout from server');
    }
  };

  const getIdToken = async () => {
    if (!currentUser) return null;

    if (isNative()) {
      const { claims } = await currentUser.getIdTokenResult();
      const timeLeft = claims.iat ? calculateTimeLeft(parseInt(claims.iat)) : 0;
      if (timeLeft < 5 * 60 * 1000) {
        // On mobile, we need to force refresh token based on our custom expiry time
        // to ensure persisent login
        return currentUser.getIdToken(true);
      }
    }
    return currentUser.getIdToken();
  };

  useEffect(() => {
    if (!router.isReady) return;
    const signUpPaths = ['/signup', '/get-started'];
    const currentPath = router.asPath;
    const isSignUpPath = signUpPaths.some((path) => currentPath.includes(path));
    setIsSignUpPath(isSignUpPath);
  }, [router.asPath, router.isReady]);

  useEffect(() => {
    const mobileApp = isNative();
    if (mobileApp || !currentUser || isSignUpPath) return;
    // activity listeners to reset the logout timer
    restartLogoutTimer();
    const events = [
      'mousemove',
      'keydown',
      'click',
      'scroll',
      'focus',
      'touchstart',
    ];
    for (const event of events) {
      window.addEventListener(event, restartLogoutTimer);
    }
    return () => {
      for (const event of events) {
        window.removeEventListener(event, restartLogoutTimer);
      }
    };
  }, [currentUser, isSignUpPath, restartLogoutTimer]);

  const login = async (
    email: string,
    password: string,
    otp?: string,
    landingPagePath?: string,
    trustDevice?: boolean,
  ) => {
    if (!auth) throw new Error('Auth not initialized');
    setIsPending(true);
    setIsSuccess(false);
    try {
      const result = await fetchLoginToken({
        email,
        password,
        otp,
        landingPagePath,
        trustDevice: otp && trustDevice ? true : undefined,
        trustToken: otp ? undefined : await getTrustedDeviceToken(),
      });

      if (result.status === 'OTP_SENT') {
        return {
          resultStatus: result.status,
          validatedMobilePhone: result.validatedMobilePhone,
          user: null,
          err: null,
        };
      }
      if (result.status === 'SUCCESSFUL_LOGIN_2FA' && result.trustToken) {
        await setTrustedDeviceToken(result.trustToken);
      }
      const user = await signInWithCustomToken(
        auth,
        result.firebaseCustomToken,
      );
      setIsSuccess(true);

      return {
        resultStatus: result.status,
        user,
        err: null,
        offer: result.offer ?? null,
        landingPageOffer: result.landingPageOffer ?? null,
      };
    } catch (error) {
      return {
        resultStatus: 'ERROR' as const,
        err: getAuthErrorCode(error),
        user: null,
      };
    } finally {
      setIsPending(false);
    }
  };

  const logout = async () => {
    if (!auth) throw new Error('Auth not initialized');
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Error during signOut:', error);
      return { success: false, err: 'Logout failed' };
    }
    return { success: true };
  };

  const onSessionEnd = async () => {
    setIsPending(true);
    setIsSuccess(false);
    try {
      if (isNative()) {
        await disableIterableDevice();
        const keysToPreserve = [
          INTRO_SCREENS_SEEN_KEY,
          THEME_PREFERENCE_KEY,
          TRUSTED_DEVICE_TOKEN_KEY,
        ];
        const preserved = await Promise.all(
          keysToPreserve.map((key) => Preferences.get({ key })),
        );
        // Await the clear so IdentityBoot's post-logout refresh can't race
        // a still-pending write and re-read a stale PIN_KEY / BIOMETRIC_ENABLED.
        await Preferences.clear();
        await Promise.all(
          keysToPreserve.map((key, index) =>
            preserved[index].value !== null
              ? Preferences.set({ key, value: preserved[index].value })
              : Promise.resolve(),
          ),
        );
        localStorage.removeItem('fcm_token');
      } else {
        clearLogoutTimer();
        await logoutOfWA();
      }
      localStorage.removeItem('pb_uid');
      localStorage.removeItem('transferStore');
      clearStrongerNudgeId();
      clearStrongerNudgeStageStack();
      clearCompletionRoute();
      clearJourney();
      clearBackRoute();
      clearPendingRedirect();
      clearPendingConversion();
      await queryClient.invalidateQueries();
      pushEventToDataLayer('user_logged_out');
      setIsPending(false);
    } catch (error: unknown) {
      console.warn('Logout side effects error:', error);
      setIsPending(false);
    }
  };

  const onSessionEndRef = React.useRef(onSessionEnd);
  useEffect(() => {
    onSessionEndRef.current = onSessionEnd;
  });

  const resetPassword = async (email: string) => {
    if (!auth) throw new Error('Auth not initialized');
    setIsPending(true);
    try {
      await checkEmailSync({ email });

      await sendPasswordResetEmail(auth, email);
      setIsPending(false);
      return { result: 'navigate' } as const;
    } catch (error) {
      setIsPending(false);
      return {
        result: 'error',
        err: 'Password reset failed: ' + error,
      } as const;
    }
  };

  useEffect(() => {
    if (!auth) return;

    const beforeAuthStateChanged = auth.beforeAuthStateChanged(async (user) => {
      try {
        if (!user) {
          console.log('User logged out. Running onSessionEnd side effects.');

          await onSessionEndRef.current();
        }
      } catch (error) {
        console.error('Error during onSessionEnd:', error);
      }
    });

    const onAuthStateChanged = auth.onAuthStateChanged(async (user) => {
      if (user) {
        console.log('Auth state changed. New User:', user?.email);
      }

      if (!user) {
        console.log('No user is logged in.');
      }

      setIsLoggedIn(!!user);
      setCurrentUser(user);
      setFirebaseAuthReady(true);
      const uid = user?.uid ?? null;

      if (uid) {
        pushEventToDataLayer('user_identified', {
          uid,
          user_id: uid,
        });
      }

      publishAppEvent('AUTH_STATE_CHANGED', { user_id: uid });

      setIsPending(false);
    });
    return () => {
      onAuthStateChanged();
      beforeAuthStateChanged();
    };
  }, [auth]);

  const value = useMemo(
    () => ({
      auth,
      isPending,
      isSuccess,
      isLoggedIn,
      currentUser,
      authReady,
      login,
      logout,
      resetPassword,
      getIdToken,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isPending, isSuccess, isLoggedIn, currentUser, authReady],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
